import { ClipboardList, Send } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, pathFor,
  Diagram, MockTable, MockButton,
} from "../shared";

export default function PagesMeetingTasksSection() {
  return (
    <div data-testid="guide-pages-meeting-tasks">
      <PageHeading
        icon={ClipboardList}
        title="Meeting Tasks"
        description="A simple log for meetings and the tasks they create. You record the meeting details, list the action items that came out of it, then turn them all into tracked items with a single click."
      />

      <Callout variant="info" title="In plain words">
        After a meeting you usually have a list of "who-does-what". This page is where you <span className="text-white">type that list once</span> and then press one button to create proper, tracked action items for everyone — instead of registering each one by hand.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>Straight after a meeting, to capture all the agreed actions in one place.</li>
          <li>When several tasks need creating at once from the same source.</li>
          <li>For a one-off task that didn't come from a meeting, use <Link href={pathFor("pages/register")} className="text-cyan-300 hover:underline">Register</Link> instead.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Meeting Tasks"
          legend={[
            <>Each row is one meeting entry, showing its name, date, type, status (<span className="text-white">Draft</span> or <span className="text-white">Finalized</span>), and how many action items it holds. Click a row to open and edit it.</>,
            <>The green <span className="inline-flex items-center gap-1"><Send className="w-3 h-3 text-emerald-300" /> Register Tasks</span> button turns that entry's action items into tracked items all at once.</>,
            <>Once registered, each row is marked with a green <span className="text-emerald-300">Registered</span> badge — pressing the button again won't create duplicates.</>,
          ]}
        >
          <MockTable
            columns={["Meeting", "Date", "Status", "Items", ""]}
            rows={[
              ["March Board Meeting", "14 Mar", "Finalized", "5", <MockButton label="Register Tasks" marker={2} variant="ghost" />],
              ["IC Weekly", "20 Mar", "Draft", "3", <span className="text-emerald-300">Registered</span>],
            ]}
          />
          <p className="text-2xs text-white/40">Click any row to open it. The "Items" count and status sit on the left.</p>
        </Diagram>
        <Steps
          steps={[
            <>Find or open the <span className="text-white">meeting entry</span> you want (rows are sorted by meeting date). Click it to edit its details and action-item list.</>,
            <>When the list is ready, press <span className="text-white">Register Tasks</span> to create tracked items from every row in one go.</>,
            <>Check for the green <span className="text-emerald-300">Registered</span> badge — that confirms it's done, and stops you registering the same rows twice.</>,
          ]}
        />
      </Section>

      <Section title="What 'Register Tasks' does behind the scenes">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>Sets the origin type to a meeting type and the origin reference to this entry's reference.</li>
          <li>Carries through each task's title, instruction, assignee, due date, priority, lane, and sign-off setting.</li>
          <li>Emails each assignee, showing both who logged it and who sent it (when those differ).</li>
        </ul>
      </Section>

      <Callout variant="info">
        For the form-filling walkthrough, see <Link href={pathFor("forms/meeting-tasks")} className="text-cyan-300 hover:underline">Log Meeting Tasks</Link>.
      </Callout>
    </div>
  );
}
